import {
  FEEDBACK_STATES,
  GAME_ID,
  ROUND_STATES,
  REDUX_ACTIONS,
  NUM_ONBOARDING_SCREENS,
} from 'a/bookClub/data/constants';
import {
  selectAvailableQuestionSetIds,
  isLastQuestion,
  getQuestionSet,
} from './curriculumSelectors';

const initialState = {
  // Curriculum indexes
  currentLevelIndex: 0,
  currentQuestionSetId: '',
  currentQuestionIndex: 0,
  // UI state values
  isScreenBlocked: false,
  // Round play values
  feedbackState: FEEDBACK_STATES.NONE,
  roundState: ROUND_STATES.NO_ACTIVE_ROUND,
  responseAttemptCount: 0,
  selectedResponseId: undefined, // What answer did user most recently give
  characterIntroScreenTextIndex: 0, // What index of screen are we on in the character intro
  onboardingActive: false,
  hasCompletedOnboarding: false,
  currentOnboardScreenIndex: 0,
  returnAfterOnboarding: false,
  questionsAnsweredThisRound: 0,
  alyxState: 0,
  // the below is used for disabling options the user has already answered incorrectly this round
  currentItemIncorrectAnswers: [false, false, false],
  hasCompletedItems: false,
  hasMasteredRound: false,
  isStampShowing: false,
  progressAmount: 0,
};

const roundPlayReducer = (state = initialState, { gameId, type, payload }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  switch (type) {
    case REDUX_ACTIONS.END_FEEDBACK_PHASE:
      if (state.roundState === ROUND_STATES.FEEDBACK) {
        if (state.feedbackState === FEEDBACK_STATES.NEGATIVE) {
          return {
            ...state,
            feedbackState: FEEDBACK_STATES.NONE,
            roundState: ROUND_STATES.QUESTION_ITEM,
            isScreenBlocked: false,
          };
        } if (state.feedbackState === FEEDBACK_STATES.POSITIVE) {
          // If student was correct and there are no more question items, end the round
          if (isLastQuestion(state.currentLevelIndex, state.currentQuestionIndex)) {
            return {
              ...state,
              feedbackState: FEEDBACK_STATES.NONE,
              roundState: ROUND_STATES.NO_ACTIVE_ROUND,
              isScreenBlocked: false,
              currentItemIncorrectAnswers: [false, false, false],
            };
          }
          // If student was correct and there are more question items, advance to next item
          return {
            ...state,
            feedbackState: FEEDBACK_STATES.NONE,
            roundState: ROUND_STATES.QUESTION_ITEM,
            currentQuestionIndex: state.currentQuestionIndex + 1,
            currentItemIncorrectAnswers: [false, false, false],
            isScreenBlocked: false,
            responseAttemptCount: 0,
          };
        }
      }
      return state;
    case REDUX_ACTIONS.END_ROUND:
      // Reset to a fresh round (aka initial) state when a round ends
      return {
        ...initialState,
        currentQuestionSetId: state.currentQuestionSetId,
        currentLevelIndex: state.currentLevelIndex,
        characterIntroScreenTextIndex: 0,
        hasCompletedOnboarding: state.hasCompletedOnboarding,
        returnAfterOnboarding: state.returnAfterOnboarding,
        hasCompletedItems: false,
        hasMasteredRound: false,
        isStampShowing: false,
      };
    case REDUX_ACTIONS.SELECT_RESPONSE_BOOK: {
      // Response only gets "correct" credit if its the users first try on this question (in tihs round)
      const incorrectAnswers = [...state.currentItemIncorrectAnswers];
      if (!payload.isCorrect) {
        incorrectAnswers[payload.optionIndex] = true;
      }
      return {
        ...state,
        roundState: ROUND_STATES.FEEDBACK,
        feedbackState: payload.isCorrect ? FEEDBACK_STATES.POSITIVE : FEEDBACK_STATES.NEGATIVE,
        isScreenBlocked: true,
        responseAttemptCount: state.responseAttemptCount + 1,
        selectedResponseId: payload.responseId,
        questionsAnsweredThisRound: state.questionsAnsweredThisRound + 1,
        progressAmount: payload.isCorrect ? state.progressAmount + 25 : state.progressAmount,
        currentItemIncorrectAnswers: incorrectAnswers,
      };
    }
    case REDUX_ACTIONS.START_ROUND:
      return {
        ...state,
        currentLevelIndex: payload.levelIndex,
        currentQuestionIndex: 0,
        progressAmount: 0,
        roundState: ROUND_STATES.QUESTION_ITEM,
        characterIntroScreenTextIndex: 0,
        onboardingActive: !state.hasCompletedOnboarding,
        returnAfterOnboarding: false,
        questionsAnsweredThisRound: 0,
        currentItemIncorrectAnswers: [false, false, false],
        hasCompletedItems: false,
        hasMasteredRound: false,
        isStampShowing: false,
      };
    case REDUX_ACTIONS.NEXT_CHARACTER_INTRO_TEXT:
      return {
        ...state,
        characterIntroScreenTextIndex: state.characterIntroScreenTextIndex + 1,
      };
    case REDUX_ACTIONS.ADVANCE_ONBOARDING_SCREEN:
      if (state.currentOnboardScreenIndex === NUM_ONBOARDING_SCREENS - 1) {
        return {
          ...state,
          hasCompletedOnboarding: true,
          onboardingActive: false,
          currentOnboardScreenIndex: 0,
          roundState: state.returnAfterOnboarding ? ROUND_STATES.NO_ACTIVE_ROUND : ROUND_STATES.QUESTION_ITEM,
        };
      }
      return {
        ...state,
        currentOnboardScreenIndex: state.currentOnboardScreenIndex + 1,
      };
    case REDUX_ACTIONS.REPEAT_ONBOARDING:
      return {
        ...state,
        currentQuestionIndex: 0,
        currentLevelIndex: 0,
        roundState: ROUND_STATES.QUESTION_ITEM,
        currentQuestionSetId: selectAvailableQuestionSetIds(0)[0],
        returnAfterOnboarding: true,
        onboardingActive: true,
        currentOnboardScreenIndex: 0,
      };
    case REDUX_ACTIONS.SET_ALYX_STATE:
      return {
        ...state,
        alyxState: payload.alyxState,
      };
    case REDUX_ACTIONS.COMPLETE_ITEMS:
      return {
        ...state,
        hasCompletedItems: true,
        hasMasteredRound: payload.isSuccess,
      };
    case REDUX_ACTIONS.SHOW_STAMP:
      return {
        ...state,
        isStampShowing: true,
      };
    case REDUX_ACTIONS.QUICK_ROUND:
      return {
        ...state,
        currentQuestionIndex: getQuestionSet(payload.level).questions.length - 1,
        currentLevelIndex: payload.level,
        hasCompletedItems: true,
      };
    default:
      return state;
  }
};

export default roundPlayReducer;
